import axios from 'axios'
import { API_BASE } from './apiBase'

const api = axios.create({
  baseURL: API_BASE,
  // Edge resolve can be slow on flaky mobile networks (seen 20s+ on cold
  // function starts); aborting early showed a false "timed out" for links
  // that were seconds away from resolving. 60s keeps UX honest.
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
})

export interface ResolveResult {
  success: true
  type: 'hls' | 'dash' | 'mp4' | 'webm' | 'mov' | 'direct'
  source: string
  sources?: Array<{ type: string; url: string; quality?: string; label?: string }>
  title?: string
  thumbnail?: string
  duration?: number
  subtitles?: Array<{ label: string; language: string; url: string }>
  provider?: string
  drmProtected?: boolean
}

export interface ResolveError {
  success: false
  error: string
  code: string
}

export type ResolveResponse = ResolveResult | ResolveError

// ---------------------------------------------------------------------------
// Resolve cache + prefetch
// ---------------------------------------------------------------------------
// /api/resolve is the biggest single chunk of "video takes a while to start":
// it runs AFTER the user taps Play, and cloud links (TeraBox/Jotted) need
// several upstream round trips server-side before the player even gets a URL.
// Caching the answer for a few minutes and warming it while the user is still
// typing/pasting moves that wait off the critical path - the tap then resolves
// from memory (and the TLS handshake to the edge is already warm).
const RESOLVE_CACHE_TTL = 3 * 60_000
const resolveCache = new Map<string, { result: ResolveResponse; at: number }>()
const inflight = new Map<string, Promise<ResolveResponse>>()

// Shapes that mean "this URL is complete, start resolving it now" - used to
// avoid firing a request for every half-typed URL.
const COMPLETE_LINK = new RegExp(
  [
    '\\.(mp4|m4v|webm|mov|mkv|avi|3gp|ogg|m3u8|mpd)([?#]|$)',
    '[?&]surl=',
    '/s/[A-Za-z0-9_-]{6,}/?$',
    '/app/[0-9a-f]{16,}',
    '/sharing/(link|embed)\\b',
    'terabox|terafile|dubox|4funbox|mirrobox|momerybox|tibibox|hugebox|jotted|diskwala',
    'youtu\\.be|youtube\\.com',
    'vimeo\\.com/\\d+',
    'fb\\.watch|facebook\\.com'
  ].join('|'),
  'i'
)

/**
 * Start resolving in the background so a later resolveVideo() call is instant.
 * @param complete set when the caller KNOWS the URL is finished (paste, drop,
 *        example/history button) - skips the "looks complete" heuristic.
 */
export function prefetchResolve(url: string, complete = false): void {
  const key = url.trim()
  if (!/^https?:\/\/\S{8,}/i.test(key)) return
  if (!complete && !COMPLETE_LINK.test(key)) return
  void resolveVideo(key).catch(() => undefined)
}

export async function resolveVideo(url: string): Promise<ResolveResponse> {
  const key = url.trim()

  const hit = resolveCache.get(key)
  if (hit && Date.now() - hit.at < RESOLVE_CACHE_TTL) return hit.result

  // A prefetch (or a previous tap) may already be running for this URL -
  // join it instead of asking the edge twice.
  const pending = inflight.get(key)
  if (pending) return pending

  const job = requestResolve(key)
    .then(result => {
      // Only successes are cached: a failed resolve should be retryable.
      if (result.success) resolveCache.set(key, { result, at: Date.now() })
      return result
    })
    .finally(() => { inflight.delete(key) })
  inflight.set(key, job)
  return job
}

async function requestResolve(url: string): Promise<ResolveResponse> {
  // This deployment is served from multiple edge IPs; one pair currently
  // answers with a wrong TLS certificate (ERR_CERT_COMMON_NAME_INVALID),
  // which fails instantly while the next connection may land on a healthy
  // edge. Retry the instant network-failure case before surfacing an error.
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))
  let lastError: ResolveError = {
    success: false,
    error: 'Network error. Please check your connection.',
    code: 'NETWORK_ERROR'
  }
  for (let attempt = 0; attempt < 4; attempt++) {
    if (attempt > 0) await sleep(400 * attempt)
    try {
      const res = await api.post<ResolveResponse>('/resolve', { url, device_id: getDeviceId() })
      return res.data
    } catch (err: any) {
      if (err.response?.data) return err.response.data as ResolveError
      if (err.code === 'ECONNABORTED') {
        // Real timeout: the edge was slow, retrying would only stall longer.
        return { success: false, error: 'Request timed out. Please try again.', code: 'TIMEOUT' }
      }
      lastError = {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
  return lastError
}

export async function getAdminStats(token: string) {
  const res = await api.get('/admin/stats', { headers: { Authorization: `Bearer ${token}` } })
  return res.data
}

export async function adminLogin(username: string, password: string) {
  const res = await api.post('/admin/login', { username, password })
  return res.data
}

export async function getRecentRequests(token: string, limit = 50) {
  const res = await api.get(`/admin/requests?limit=${limit}`, { headers: { Authorization: `Bearer ${token}` } })
  return res.data
}

export async function getProviders(token: string) {
  const res = await api.get('/admin/providers', { headers: { Authorization: `Bearer ${token}` } })
  return res.data
}

export async function getServerHealth(token: string) {
  const res = await api.get('/admin/health', { headers: { Authorization: `Bearer ${token}` } })
  return res.data
}

// First-party device id so the admin panel can group link pastes per device.
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem('dvp_device_id')
    if (!id) {
      id = typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem('dvp_device_id', id)
    }
    return id
  } catch {
    return 'unknown'
  }
}

export interface DeviceRow {
  device_id: string
  last_seen: number
  total: number
  plays: number
  ip: string
  country: string
  city: string
  ua: string
}

export interface DeviceEvent {
  id: number
  ts: number
  url: string
  provider: string | null
  success: number
  error: string | null
  ip: string
  country: string
  city: string
}

export async function getDevices(token: string): Promise<DeviceRow[]> {
  const res = await api.get('/admin/devices', { headers: { Authorization: `Bearer ${token}` } })
  return res.data
}

export async function getDeviceLog(token: string, deviceId: string): Promise<DeviceEvent[]> {
  const res = await api.get(`/admin/device?id=${encodeURIComponent(deviceId)}`, {
    headers: { Authorization: `Bearer ${token}` }
  })
  return res.data
}
